import { baseApi } from "./baseApi";

type FeedBackPayloadType = {
  rating: number;
  message: string;
};

type ContactUsRequestPayloadType = {
  email: string;
  phone: string;
  message: string;
};

type ModalitiesRequestPayloadType = {
  modality_codes: string[];
};

type FeaturesRequestPayloadType = {
  feature_codes: string[];
};

export type KamAnalyticsResponse = {
  total_users: number;
  total_organisations: number;
  features_live_count: number;
  features_total_count: number;
  modalities_live_count: number;
  modalities_total_count: number;
  product_utilisation_percentage: number;
};

export type EscalationContact = {
  department: string;
  email: string;
  meeting_link: string;
  name: string;
  phone: string;
  position: string;
  profile_picture: string;
  availability: {
    start_time: string;
    end_time: string;
  };
};

export type EscalationContactSectionDataResponse = {
  escalation_matrix: EscalationContact[];
};

export type KamModality = {
  code: string;
  title: string;
  description: string;
  is_live: boolean;
  tag: string | null;
};

export type KamModalitiesResponse = {
  modalities: KamModality[];
};

export type KamFeature = {
  code: string;
  title: string;
  description: string;
  is_live: boolean;
  tag: string | null;
};

export type KamFeaturesResponse = {
  features: KamFeature[];
};

export type MoreForYouItem = {
  code: string;
  title: string;
  description: string;
};

export type KamMoreForYouResponse = {
  items: MoreForYouItem[];
};

const kamApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getEscalationContactSectionData: builder.query<
      EscalationContactSectionDataResponse,
      void
    >({
      query: () => ({
        url: "/client_config/kam",
        method: "GET",
      }),
      providesTags: ["KamData"],
    }),
    getKamAnalytics: builder.query<KamAnalyticsResponse, void>({
      query: () => ({
        url: "/kam/analytics",
        method: "GET",
      }),
      providesTags: ["KamData"],
    }),
    getKamModalities: builder.query<KamModalitiesResponse, void>({
      query: () => ({
        url: "/kam/modalities",
        method: "GET",
      }),
      providesTags: ["KamData"],
    }),
    getKamFeatures: builder.query<KamFeaturesResponse, void>({
      query: () => ({
        url: "/kam/features",
        method: "GET",
      }),
      providesTags: ["KamData"],
    }),
    getKamMoreForYou: builder.query<KamMoreForYouResponse, void>({
      query: () => ({
        url: "/kam/more-for-you",
        method: "GET",
      }),
      providesTags: ["KamData"],
    }),
    sendFeedback: builder.mutation({
      query: (payload: FeedBackPayloadType) => ({
        url: "/dashboard/feedback",
        method: "POST",
        body: payload,
      }),
    }),
    sendContactUsRequest: builder.mutation({
      query: (payload: ContactUsRequestPayloadType) => ({
        url: "/kam/contact-us/request",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: payload,
      }),
    }),
    sendKamModalitiesRequest: builder.mutation({
      query: (payload: ModalitiesRequestPayloadType) => ({
        url: "/kam/modalities/request",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: payload,
      }),
    }),
    sendKamFeaturesRequest: builder.mutation({
      query: (payload: FeaturesRequestPayloadType) => ({
        url: "/kam/features/request",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: payload,
      }),
    }),
  }),
});

export const {
  useGetEscalationContactSectionDataQuery,
  useGetKamAnalyticsQuery,
  useGetKamFeaturesQuery,
  useGetKamMoreForYouQuery,
  useGetKamModalitiesQuery,
  useSendContactUsRequestMutation,
  useSendKamFeaturesRequestMutation,
  useSendKamModalitiesRequestMutation,
  useSendFeedbackMutation,
} = kamApi;
